import noop from 'lodash/noop'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { UseFormReturn } from '../../../../../hooks'
import { InvoiceEditorVariant } from '../enums/invoiceEditorVariant'
import { RecurringInvoiceFormSchema } from '../types/recurringInvoiceForm/recurringInvoiceFormSchema'
import { useInvoiceEditor } from './invoiceEditorContext'
import { useInvoiceEditorForms } from './invoiceEditorFormsContext'
import { useInvoiceEditorValues } from './invoiceEditorValuesContext'

interface ContextState {
  recurringInvoiceForm: UseFormReturn<RecurringInvoiceFormSchema>
  validate: () => Promise<boolean>
}

const RecurringInvoiceFormContext = createContext<ContextState | undefined>(undefined)

interface RecurringInvoiceFormContextProviderProps {
  children?: ReactNode
}

export const RecurringInvoiceFormContextProvider = ({
  children,
}: RecurringInvoiceFormContextProviderProps): ReactElement | RecurringInvoiceFormContextProviderProps['children'] => {
  const { recurringInvoiceForm } = useInvoiceEditorForms()
  const { invoiceEditorVariant } = useInvoiceEditor()
  const { isLoading, recurringInvoiceValues } = useInvoiceEditorValues()

  // the variant the form currently holds a schedule for
  const seededEditorVariant = useRef<InvoiceEditorVariant | undefined>()

  const { Form: RecurringInvoiceFormProvider, handleSubmit, reset, trigger } = recurringInvoiceForm

  // we're using a fake submit for validation due to how useForm's mode param works
  const handleValidation = useCallback(async () => {
    await handleSubmit(noop)()
    return trigger()
  }, [handleSubmit, trigger])

  // The schedule of an existing recurring invoice arrives with the loaded document and carries
  // its id - which is what tells the save whether to update the profile or create a new one.
  // Seeding once per variant is deliberate: recurringInvoiceValues is rebuilt whenever the
  // document is refetched, and re-seeding would throw away the user's edits. It also means the
  // schedule survives switching the recurring invoice toggle off and back on.
  useEffect(() => {
    const isFormSeededForVariant = seededEditorVariant.current === invoiceEditorVariant

    if (isLoading || !recurringInvoiceValues || isFormSeededForVariant) {
      return
    }

    seededEditorVariant.current = invoiceEditorVariant
    reset(recurringInvoiceValues)
  }, [invoiceEditorVariant, isLoading, recurringInvoiceValues, reset])

  const contextValue = useMemo(
    () => ({
      recurringInvoiceForm,
      validate: handleValidation,
    }),
    [handleValidation, recurringInvoiceForm],
  )

  return (
    <RecurringInvoiceFormContext.Provider value={contextValue}>
      <RecurringInvoiceFormProvider>{children}</RecurringInvoiceFormProvider>
    </RecurringInvoiceFormContext.Provider>
  )
}

export const useRecurringInvoiceForm = () => {
  const context = useContext(RecurringInvoiceFormContext)

  if (!context) {
    throw new Error('RecurringInvoiceFormContextProvider is missing in the module!')
  }

  return context
}
